<template>
	<div>
		<BaseLoading v-if="isLoadingDetail" />
		<template v-else>
			<!-- Head of General Information -->
			<CRow>
				<CCol xl="12">
					<p class="text-header">
						Branch name: {{ detailInfo.branchName }}
					</p>
				</CCol>
			</CRow>
			<!-- End Head of General Information -->

			<!-- General Information -->
			<CRow>
				<CCol xl="10" class="pt-3">
					<CRow>
						<CCol xl="3">
							<CRow>
								<CCol xl="12">
									<p class="text-label m-0">
										Branch id:
									</p>
									<p class="text-desc mb-2">
										{{ detailInfo.itecBranchId }}
									</p>
								</CCol>
								<CCol xl="12">
									<p class="text-label m-0">
										Location:
									</p>
									<p class="text-desc mb-2">
										{{ detailInfo.location }}
									</p>
								</CCol>
							</CRow>
						</CCol>
						<CCol xl="3">
							<CRow>
								<CCol xl="12">
									<p class="text-label m-0">
										Total Stock:
									</p>
									<p class="text-desc mb-2">
										{{ detailInfo.totalStock }}
										{{ totalAllocatedStockQty }}
									</p>
								</CCol>
								<CCol xl="12">
									<p class="text-label m-0">
										Total brands:
									</p>
									<p class="text-desc mb-2">
										{{ detailInfo.totalBrand }}
									</p>
								</CCol>
								<CCol xl="12">
									<p class="text-label m-0">
										Total SKU:
									</p>
									<p class="text-desc">
										{{ detailInfo.totalSku }}
									</p>
								</CCol>
							</CRow>
						</CCol>
						<CCol xl="3">
							<CRow>
								<CCol xl="12">
									<p class="text-label m-0">
										Allocation to:
									</p>
									<p class="text-desc mb-0">
										Online Channels:
										<span class="default-value-dat">{{
											onlineStockQty
										}}</span>
									</p>
									<div
										v-if="onlineStock.items.length"
										class="container-text-box mt-2 mb-4"
									>
										<p
											v-for="(item,
												index) in onlineStock.items"
											:key="index"
											class="text-desc"
										>
											<span class="default-value-dat">
												{{ item.name }}
												{{ item.percentage }}% ({{
													item.quantity | numberFormat
												}}
												Qty)
											</span>
										</p>
									</div>
									<p class="text-desc mb-0">
										Offline Channels:
										<span class="default-value-dat">{{
											offlineStockQty
										}}</span>
									</p>
									<div
										v-if="offlineStock.items.length"
										class="container-text-box mt-2 mb-2"
									>
										<p
											v-for="(item,
												index) in offlineStock.items"
											:key="index"
											class="text-desc"
										>
											<span class="default-value-dat">
												{{ item.name }}
												{{ item.percentage }}% ({{
													item.quantity | numberFormat
												}}
												Qty)
											</span>
										</p>
									</div>
								</CCol>
							</CRow>
						</CCol>
					</CRow>
				</CCol>
			</CRow>
			<!-- End General Information -->
		</template>

		<!-- Head of Table -->
		<CRow>
			<CCol xl="12">
				<p class="text-header">
					Available SKU in this branch
				</p>
			</CCol>
		</CRow>
		<!-- End Head of Table -->

		<!-- Filter -->
		<!-- Search Filter -->
		<BaseSearchFilter :active="isActive" :show="isActive">
			<template #header>
				<CCol>
					<CInput
						v-model="query.keyword"
						:placeholder="
							$t('placeholder.stock_browser_branch_detail')
						"
						@keyup.enter="onInputEnter"
					>
						<template #prepend-content>
							<FontAwesomeIcon :icon="['far', 'search']" />
						</template>
					</CInput>
				</CCol>
			</template>
			<template #body>
				<CCol lg="3">
					<BaseDropDown
						v-model="filter.brand"
						:options="brandOptions"
						:label-drop-down="$t('label.brand')"
						:placeholder="$t('message.selection')"
						:searchable="true"
						label="name"
						track-by="value"
						@input="handleBrandChange"
					/>
				</CCol>
				<CCol lg="9">
					<products-filter-category
						:category-id="query.category"
						@onCategoryChange="handleCategory"
					/>
				</CCol>
				<CCol lg="3">
					<BaseDropDown
						v-model="filter.status"
						:options="SKU_STATUS_OPTIONS"
						:label-drop-down="$t('label.status')"
						:placeholder="$t('message.selection')"
						:searchable="false"
						label="name"
						track-by="value"
						@input="handleStatusChange"
					/>
				</CCol>
				<CCol md="3">
					<BaseDropDown
						v-model="filter.type"
						:options="SKU_TYPE_OPTIONS"
						:label-drop-down="$t('label.product_type')"
						:placeholder="$t('message.selection')"
						:searchable="false"
						label="name"
						track-by="value"
						@input="handleTypeChange"
					/>
				</CCol>
			</template>
			<template #footer>
				<CCol md="8">
					{{ $t("message.items_found", { number: totalCount }) }}
				</CCol>
				<CCol md="4">
					<button @click="onResetClick">
						{{ $t("button.resetFilters") }}
					</button>
				</CCol>
			</template>
		</BaseSearchFilter>
		<!-- End Filter -->

		<!-- Table, Pagination -->
		<CRow>
			<CCol xl="12">
				<BaseTable
					:is-loading="isLoadingTable"
					:pagination="pagination"
					:fields="STOCK_BROWSER_BRANCH_DETAIL_TABLE_FIELDS"
					:items="dataTable"
					:hide-columns="['id']"
					:sort-columns="{
						sku:
							query.sort_by === 'sku'
								? query.sort_direction || 'asc'
								: 'asc',
						product_name:
							query.sort_by === 'product_name'
								? query.sort_direction || 'asc'
								: 'asc'
					}"
					hover
					fixed
					class="table-custom"
					@onSortClick="handleSortClick"
					@onPaginationClick="handlePageClick"
				>
					<template #no-items-view>
						<div class="empty-table-element">
							<p class="subtitle">
								{{ $t("message.notFoundSkuList") }}
							</p>
						</div>
					</template>
					<template #image="{ item = '' }">
						<img
							:src="item || '/img/product-placeholder.svg'"
							width="40"
							height="40"
						>
					</template>
					<template #category="{ item = '' }">
						<p
							v-for="(value, index) in item"
							:key="index"
							class="m-2"
						>
							{{ value }}
						</p>
					</template>
					<template #status="{ item = '' }">
						<CBadge class="badge-sku-status" :color="item.color">
							{{ item.title }}
						</CBadge>
					</template>
				</BaseTable>
			</CCol>
		</CRow>
		<!-- End Table, Pagination -->
	</div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import ProductsFilterCategory from '../components/ProductsFilterCategory.vue';
import {
	randomSearchString,
	cleanObject,
	cloneDeep,
	numberFormat,
} from '../helpers/utils';
import { getSelectedValue } from '../helpers/dropdown';
import { STOCK_BROWSER_BRANCH_DETAIL_TABLE_FIELDS } from '../enums/stock';
import { ROUTER_NAME } from '../enums/router';
import {
	SKU_STATUS_COLOR,
	SKU_STATUS_TITLE,
	SKU_STATUS_OPTIONS,
	SKU_TYPE_OPTIONS,
} from '../enums/sku';

export default {
	name: 'StockBrowserBranchSingle',

	components: {
		ProductsFilterCategory,
	},

	data() {
		return {
			STOCK_BROWSER_BRANCH_DETAIL_TABLE_FIELDS,
			SKU_STATUS_OPTIONS,
			SKU_TYPE_OPTIONS,
			query: {
				keyword: this.$route.query.keyword || null,
				sort_by: this.$route.query.sort_by || null,
				sort_direction: this.$route.query.sort_direction || null,
				brand: this.$route.query.brand || null,
				status: this.$route.query.status || null,
				type: this.$route.query.type || null,
				category: this.$route.query.category || null,
				page: this.$route.query.page || null,
				r: randomSearchString(),
			},
			filter: {
				brand: getSelectedValue(null, this.brandOptions),
				status: getSelectedValue(
					this.$route.query.status,
					SKU_STATUS_OPTIONS,
				),
				type: getSelectedValue(this.$route.query.type, SKU_TYPE_OPTIONS),
			},
		};
	},

	computed: {
		...mapState('stockBrowserBranchSingle', {
			detailInfo: 'data',
			isLoadingDetail: 'isLoading',
		}),
		...mapState('stockBrowserBranchSkuList', {
			skuList: 'data',
			isLoadingTable: 'isLoading',
			pagination: 'pagination',
		}),
		...mapState('channelList', {
			channelsList: 'data',
		}),
		...mapGetters('brandList', {
			brandOptions: 'brandOptions',
		}),
		...mapState('categoryList', {
			categoriesList: 'data',
		}),
		dataTable() {
			return this.skuList.map((item) => {
				return {
					id: item.id,
					image: item.imageThumbnail,
					sku: item.sku,
					product_name: item.productName,
					brand: item.brand,
					category: item.categories,
					stock: item.availableStock,
					status: {
						title: SKU_STATUS_TITLE[item.status],
						color: SKU_STATUS_COLOR[item.status],
					},
					type: item.type,
					last_synced: item.lastSynced,
				};
			});
		},
		totalCount() {
			return numberFormat(this.pagination.total);
		},
		onlineStock() {
			return this.detailInfo.onlineStock;
		},
		offlineStock() {
			return this.detailInfo.offlineStock;
		},
		onlineStockQty() {
			return `${this.onlineStock.percentage}% (${numberFormat(this.onlineStock.totalQuantity)} Qty)`;
		},
		offlineStockQty() {
			return `${this.offlineStock.percentage}% (${numberFormat(this.offlineStock.totalQuantity)} Qty)`;
		},
		totalAllocatedStockQty() {
			return `(Allocated ${numberFormat(this.detailInfo.totalAllocatedStock)} Qty)`;
		},
		// check any dropdowns are activated
		isActive() {
			if (this.filter.brand?.value !== null) return true;
			if (this.filter.status?.value !== null) return true;
			if (this.filter.type?.value !== null) return true;
			if (this.query.category !== null) return true;

			return false;
		},
	},

	async created() {
		await this.fetchData();

		// set page title
		this.setPageTitle({
			title: this.detailInfo.branchName,
			isShow: true,
		});

		// we have to clear old the breadcrumbs first
		this.clearBreadcrumb({ startAtIndex: 1 });
		// then add new breadcrumb
		this.addBreadcrumb({
			title: this.detailInfo.branchName,
			route: {
				name: ROUTER_NAME.STOCK_BROWSER_BRANCH_SINGLE,
				params: { id: this.$route.params.id },
			},
		});

		this.filter.brand = getSelectedValue(
			this.$route.query.brand
				? parseInt(this.$route.query.brand, 10)
				: null,
			this.brandOptions,
		);
	},

	methods: {
		...mapActions('stockBrowserBranchSingle', {
			getBranchStocksDetail: 'getBranchStocksDetail',
		}),
		...mapActions('stockBrowserBranchSkuList', {
			getBranchStocksSkus: 'getBranchStocksSkus',
		}),
		...mapActions('pageTitle', {
			setPageTitle: 'setPageTitle',
		}),
		...mapActions('breadcrumb', {
			addBreadcrumb: 'addBreadcrumb',
			clearBreadcrumb: 'clearBreadcrumb',
		}),
		...mapActions('channelList', {
			getChannels: 'getChannels',
		}),
		...mapActions('categoryList', {
			getCategories: 'getCategories',
		}),
		...mapActions('brandList', {
			getBrands: 'getBrands',
		}),
		async fetchData() {
			const list = [];
			const isLoadedDetail =
				(this.query.keyword ||
					this.query.page ||
					this.query.sort_by ||
					this.query.category ||
					this.query.brand ||
					this.query.status ||
					this.query.type) &&
				this.detailInfo.branchName;
			const isLoadedChannels = this.channelsList.length > 0;
			const isLoadedBrands = this.brandOptions.length > 1;
			const isLoadedCategories = this.categoriesList.length > 0;

			if (!isLoadedDetail) {
				list.push(this.getBranchStocksDetail({ id: this.$route.params.id }));
			}

			if (!isLoadedChannels) {
				list.push(this.getChannels());
			}

			if (!isLoadedBrands) {
				list.push(this.getBrands());
			}

			if (!isLoadedCategories) {
				list.push(this.getCategories());
			}

			await Promise.all([
				...list,
				this.getBranchStocksSkus({
					id: this.$route.params.id,
					params: this.query,
				}),
			]);
		},
		onInputEnter(e) {
			this.query.keyword = e.target.value;
			this.query.page = null;
			this.updateUrl();
		},
		handleSortClick(key = '', state = false) {
			this.query.page = null;
			this.query.sort_by = key;
			this.query.sort_direction = state;
			this.updateUrl();
		},
		handlePageClick(pageNum) {
			this.query.page = pageNum;
			this.updateUrl();
		},
		handleCategory(id) {
			this.query.page = null;
			this.query.category = id;
			this.updateUrl();
		},
		updateUrl() {
			const query = cleanObject({
				// always put tab, page queries to the `newQuery`
				tab: this.$route.query.tab,
				...cloneDeep(this.query),
			});
			this.$router.push({ query }).catch(() => {});
		},
		getStockInfo(percentage, quantity) {
			const percnt =
				percentage !== null ? `${numberFormat(percentage)}%` : '';
			const qty =
				quantity !== null ? `(${numberFormat(quantity)} Qty)` : '';
			const val = `${percnt} ${qty}`;
			return val.trim();
		},
		onResetClick() {
			this.query.keyword = null;
			this.query.page = null;
			this.query.r = null;
			this.query.category = null;
			this.query.brand = null;
			this.query.status = null;
			this.query.type = null;
			this.updateUrl();
		},
		handleBrandChange(option) {
			this.query.brand = option?.value ?? null;
			this.query.page = null;
			this.updateUrl();
		},
		handleStatusChange(option) {
			this.query.status = option?.value ?? null;
			this.query.page = null;
			this.updateUrl();
		},
		handleTypeChange(option) {
			this.query.type = option?.value ?? null;
			this.query.page = null;
			this.updateUrl();
		},
	},
};
</script>

<style lang="scss" scoped>
.container-text-box {
	padding: rem(8) rem(16);
	background-color: $color-gray-100;

	p {
		margin-bottom: rem(4);

		&:last-of-type {
			margin-bottom: 0;
		}
	}
}

::v-deep .table-custom {
	margin-top: rem(32);

	.image {
		// .image-col, .image-cel
		&-col,
		&-cel {
			width: rem(40);
		}

		// .image-cel
		&-cel {
			padding-right: 0;
			padding-left: 0;
		}
	}

	.sku {
		// .sku-col
		&-col {
			min-width: rem(200 + $table-cell-padding);
		}
	}

	.product-name {
		// .product-name-col
		&-col {
			min-width: rem(300 + $table-cell-padding);
		}
	}

	.brand {
		// .brand-col
		&-col {
			min-width: rem(180 + $table-cell-padding);
		}
	}

	.category {
		// .category-col
		&-col {
			min-width: rem(300 + $table-cell-padding);
		}
	}

	.type,
	.status {
		// .type-col, .status-col
		&-col {
			min-width: rem(150 + $table-cell-padding);
		}
	}

	.stock {
		// .stock-col
		&-col {
			min-width: rem(150 + $table-cell-padding);
		}
	}

	.last-synced {
		// .last-synced-col
		&-col {
			min-width: rem(150 + $table-cell-padding);
		}
	}
}

.default-value-dat:empty::before {
	content: "-";
}
</style>
